import React from 'react';
import { Users, Target, BookOpen, Sparkles, CheckCircle2 } from 'lucide-react';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

const About = () => {
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar with about as the active page */}
      <MainNavbar activePage="about" />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">About Dessino.ai</h1>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Transforming ideas into interactive prototypes with the power of AI
          </p>
        </div>
      </section>
      
      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                Dessino.ai was born from a simple realization: design tools were too complex for non-designers, and the gap between ideas and visual prototypes was too wide for most teams.
              </p>
              <p className="text-gray-600 mb-4">
                We set out to democratize the design process, making it accessible to everyone regardless of their technical background or artistic skills. By harnessing the power of artificial intelligence, we've created a platform that transforms text descriptions into fully-realized, interactive UI prototypes in minutes.
              </p>
              <p className="text-gray-600">
                Today, Dessino.ai is helping individuals and teams across industries bring their ideas to life faster than ever before, eliminating the traditional barriers between concept and visualization.
              </p>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-8 rounded-lg shadow-lg">
                <h3 className="text-xl font-bold mb-4 text-orange-600">Who We Help</h3>
                <ul className="space-y-4">
                  <li className="flex">
                    <div className="flex-shrink-0 h-6 w-6 text-orange-500 mr-3">
                      <CheckCircle2 />
                    </div>
                    <div>
                      <span className="font-semibold">Product Managers</span>
                      <p className="text-gray-600 mt-1">Visualize features before development begins and validate concepts with stakeholders</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0 h-6 w-6 text-orange-500 mr-3">
                      <CheckCircle2 />
                    </div>
                    <div>
                      <span className="font-semibold">UX/UI Designers</span>
                      <p className="text-gray-600 mt-1">Rapidly generate design alternatives and explore creative solutions without tedious manual mockups</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0 h-6 w-6 text-orange-500 mr-3">
                      <CheckCircle2 />
                    </div>
                    <div>
                      <span className="font-semibold">Startup Founders</span>
                      <p className="text-gray-600 mt-1">Create impressive prototypes for investor pitches without hiring a full design team</p>
                    </div>
                  </li>
                  <li className="flex">
                    <div className="flex-shrink-0 h-6 w-6 text-orange-500 mr-3">
                      <CheckCircle2 />
                    </div>
                    <div>
                      <span className="font-semibold">Developers</span>
                      <p className="text-gray-600 mt-1">Get clean, exportable code for implementing designs without translation errors</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Vision & Mission */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Vision & Mission</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Guided by a clear purpose and ambitious goals
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-3 rounded-full mr-4">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold">Our Vision</h3>
              </div>
              <p className="text-gray-600 mb-4">
                A world where anyone can bring their digital product ideas to life, regardless of design skills or technical expertise.
              </p>
              <p className="text-gray-600">
                We envision a future where the gap between imagination and implementation is seamlessly bridged by intelligent design tools, enabling innovation to flourish across organizations of all sizes.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <BookOpen className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold">Our Mission</h3>
              </div>
              <p className="text-gray-600 mb-4">
                To democratize the design process by creating AI-powered tools that transform text descriptions into beautiful, functional prototypes.
              </p>
              <p className="text-gray-600">
                We're committed to removing barriers to design, accelerating the product development cycle, and empowering teams to communicate ideas visually without specialized training.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide our decisions and shape our product
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 text-purple-600 mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">Accessibility</h3>
              <p className="text-gray-600">
                We believe design tools should be accessible to everyone, not just those with specialized training.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Sparkles className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">Innovation</h3>
              <p className="text-gray-600">
                We continuously push the boundaries of what's possible with AI to create ever more powerful and intuitive design tools.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-red-100 text-red-600 mb-4">
                <Target className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold mb-3">User-Centricity</h3>
              <p className="text-gray-600">
              We design our product with a relentless focus on user needs, ensuring an intuitive experience that feels effortless.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* How We Help */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">How We Help</h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Speed Up Your Design Process</h3>
              <p className="text-gray-600">
                Transform weeks of design work into minutes. Our AI generates initial designs based on your descriptions, allowing you to iterate quickly without getting bogged down in the details of manual design tools.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Bridge the Communication Gap</h3>
              <p className="text-gray-600">
                Turn abstract ideas into concrete visuals that everyone can understand. No more misinterpretations between product managers, designers, and developers—Dessino.ai creates a common visual language for your team.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Validate Ideas Faster</h3>
              <p className="text-gray-600">
                Test concepts with users and stakeholders without investing weeks in design. Create multiple alternatives quickly, gather feedback, and refine your approach before committing development resources.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold mb-3">Democratize Design Skills</h3>
              <p className="text-gray-600">
                Empower everyone on your team to contribute to the design process, regardless of their background. With Dessino.ai, anyone can translate their ideas into professional-looking prototypes.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Join Us on This Journey</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Be part of the design revolution. Try Dessino.ai today and experience how AI can transform your design process.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <a href="/signup" className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
              Get Started Free
            </a>
            <a href="/contact" className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors inline-block">
              Contact Us
            </a>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default About;